import React from 'react';
import { toast } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { accountAction, logout, getMFAStatus, regenerateMFA, confirmRegenerateMFA, disableMFA } from '../../redux/actions/userActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import Button from '../../components/Button';
import TextInput from '../../components/TextInput';
import ThemeToggle from '../../components/ThemeToggle';
import MFASetupModal from '../../components/Modal/MFASetupModal';
import { useTheme } from '../../contexts/ThemeContext';

import AccountStyles from './Acount.module.css';

const FRONTEND_ACCOUNT_ERROR_CODES = {
	PASSWORD_CHANGE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	MFA_STATUS_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	MFA_REGENERATE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	MFA_VERIFY_FAILED: ErrorCodes.API_INVALID_PARAMETER,
	MFA_DISABLE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	AUTHORIZATION_FAILED: ErrorCodes.AUTH_INSUFFICIENT_PERMISSIONS,
};

const buildAccountSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const AccountScreen = () => {
	const dispatch = useDispatch();
	const { isDarkMode } = useTheme();
	const userLogin = useSelector((state) => state.userLogin);
	const isAdmin = userLogin?.userInfo?.session?.role === 'admin';
	const username = userLogin?.userInfo?.session?.username;
	const [ currentPassword, setCurrentPassword ] = React.useState({ value: '', error: '' });
	const [ newPassword, setNewPassword ] = React.useState({ value: '', error: '' });
	const [ confirmPassword, setConfirmPassword ] = React.useState({ value: '', error: '' });

	// MFA state
	const [ mfaStatus, setMfaStatus ] = React.useState(null);
	const [ mfaLoading, setMfaLoading ] = React.useState(true);
	const [ showMFASetupModal, setShowMFASetupModal ] = React.useState(false);
	const [ mfaQrCode, setMfaQrCode ] = React.useState('');
	const [ mfaSecret, setMfaSecret ] = React.useState('');
	const [ mfaSetupError, setMfaSetupError ] = React.useState('');
	const [ mfaVerifying, setMfaVerifying ] = React.useState(false);
	const [ regeneratePassword, setRegeneratePassword ] = React.useState({ value: '', error: '' });
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);

	const setAccountSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildAccountSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearAccountSupportError = React.useCallback((force = false) => {
		if (!force) return;
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [ supportErrorMessage ]);

	const notifyAccountSupportError = React.useCallback(
		(errorCode, pageInfo, sourceMessage) => {
			const formattedError = buildAccountSupportError(errorCode, pageInfo, sourceMessage);
			toast.error(formattedError);
			setAccountSupportError(errorCode, pageInfo, sourceMessage);
		},
		[ setAccountSupportError ]
	);

	// Fetch MFA status on mount
	React.useEffect(() => {
		const fetchMFAStatus = async () => {
			try {
				setMfaLoading(true);
				const data = await dispatch(getMFAStatus());
				if (data && data.status) {
					setMfaStatus(data.mfa_status);
					clearAccountSupportError(true);
				} else {
					setMfaStatus(null);
					setAccountSupportError(
						FRONTEND_ACCOUNT_ERROR_CODES.MFA_STATUS_LOAD_FAILED,
						'Account Page / MFA Status Load Failed',
						data?.msg || (typeof data === 'string' ? data : 'MFA status request did not return success')
					);
				}
			} catch (err) {
				console.error('Error fetching MFA status:', err);
				setMfaStatus(null);
				setAccountSupportError(
					FRONTEND_ACCOUNT_ERROR_CODES.MFA_STATUS_LOAD_FAILED,
					'Account Page / MFA Status Request Error',
					err?.message || String(err)
				);
			} finally {
				setMfaLoading(false);
			}
		};

		fetchMFAStatus();
	}, [clearAccountSupportError, dispatch, setAccountSupportError]);

	const validatePassword = (password) => {
		// Matches the backend policy (app/core/password_policy.py): lowercase,
		// uppercase, digit, and one special from a broad set (includes '-', which
		// Apple's generated passwords use). Length (>=12) is checked separately.
		return /[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password)
			&& /[!@#$%^&*()\-_=+[\]{};:,.<>?\/|\\~`"']/.test(password);
	};

	const handleChangePassword = async () => {
		if (currentPassword.value === '' || currentPassword.value.trim() === '') {
			setCurrentPassword({ ...currentPassword, error: 'Please enter current password' });
		} else if (newPassword.value === '' || newPassword.value.trim() === '') {
			setNewPassword({ ...newPassword, error: 'Please enter new password' });
		} else if (!validatePassword(newPassword.value) || newPassword.value.length < 12) {
			setNewPassword({ ...newPassword, error: 'Please enter strong password' });
		} else if (newPassword.value !== confirmPassword.value) {
			setConfirmPassword({ ...confirmPassword, error: 'Passwords do not match' });
		} else {
			const formData = new FormData();
			formData.append('btn', 'Save');
			formData.append('CurrentPassword', currentPassword.value);
			formData.append('NewPassword', newPassword.value);
			formData.append('ConfirmPassword', confirmPassword.value);
			try {
				const data = await dispatch(accountAction(formData));

				if (data && data.status === true) {
					toast.success(data.msg);
					clearData();
					clearAccountSupportError(true);
				} else {
					notifyAccountSupportError(
						FRONTEND_ACCOUNT_ERROR_CODES.PASSWORD_CHANGE_FAILED,
						'Account Page / Change Password Failed',
						data?.msg || (typeof data === 'string' ? data : 'Change password request did not return success')
					);
				}
			} catch (error) {
				notifyAccountSupportError(
					FRONTEND_ACCOUNT_ERROR_CODES.PASSWORD_CHANGE_FAILED,
					'Account Page / Change Password Request Error',
					error?.message || String(error)
				);
			}
		}
	};

	const clearData = () => {
		setCurrentPassword({ value: '', error: '' });
		setNewPassword({ value: '', error: '' });
		setConfirmPassword({ value: '', error: '' });
	};

	const handleRegenerateMFA = async () => {
		if (!regeneratePassword.value || regeneratePassword.value.trim() === '') {
			setRegeneratePassword({ ...regeneratePassword, error: 'Please enter your current password' });
			return;
		}

		try {
			const data = await dispatch(regenerateMFA(regeneratePassword.value));
			if (data && data.status) {
				setMfaQrCode(data.qr_code);
				setMfaSecret(data.secret);
				setShowMFASetupModal(true);
				setRegeneratePassword({ value: '', error: '' });
				clearAccountSupportError(true);
			} else {
				setRegeneratePassword({ ...regeneratePassword, error: data.msg || 'Failed to regenerate MFA' });
				notifyAccountSupportError(
					FRONTEND_ACCOUNT_ERROR_CODES.MFA_REGENERATE_FAILED,
					'Account Page / Regenerate MFA Failed',
					data?.msg || (typeof data === 'string' ? data : 'MFA regenerate request did not return success')
				);
			}
		} catch (err) {
			setRegeneratePassword({ ...regeneratePassword, error: 'Error regenerating MFA' });
			notifyAccountSupportError(
				FRONTEND_ACCOUNT_ERROR_CODES.MFA_REGENERATE_FAILED,
				'Account Page / Regenerate MFA Request Error',
				err?.message || String(err)
			);
		}
	};

	const handleMFAVerify = async (code) => {
		try {
			setMfaSetupError('');
			setMfaVerifying(true);
			const data = await dispatch(confirmRegenerateMFA(code));
			if (data && data.status) {
				toast.success('MFA enabled successfully');
				setShowMFASetupModal(false);
				setMfaQrCode('');
				setMfaSecret('');
				setMfaVerifying(false);
				clearAccountSupportError(true);
				// Refresh MFA status
				const statusData = await dispatch(getMFAStatus());
				if (statusData && statusData.status) {
					setMfaStatus(statusData.mfa_status);
					clearAccountSupportError(true);
				} else {
					setAccountSupportError(
						FRONTEND_ACCOUNT_ERROR_CODES.MFA_STATUS_LOAD_FAILED,
						'Account Page / MFA Status Refresh Failed',
						statusData?.msg || (typeof statusData === 'string' ? statusData : 'MFA status refresh did not return success')
					);
				}
			} else {
				setMfaSetupError(data.msg || 'Invalid verification code');
				setMfaVerifying(false);
				notifyAccountSupportError(
					FRONTEND_ACCOUNT_ERROR_CODES.MFA_VERIFY_FAILED,
					'Account Page / Verify MFA Failed',
					data?.msg || (typeof data === 'string' ? data : 'MFA verify request did not return success')
				);
			}
		} catch (err) {
			setMfaSetupError('Error verifying code');
			setMfaVerifying(false);
			notifyAccountSupportError(
				FRONTEND_ACCOUNT_ERROR_CODES.MFA_VERIFY_FAILED,
				'Account Page / Verify MFA Request Error',
				err?.message || String(err)
			);
		}
	};

	const handleCloseMFAModal = () => {
		setShowMFASetupModal(false);
		setMfaQrCode('');
		setMfaSecret('');
		setMfaSetupError('');
		setMfaVerifying(false);
		setRegeneratePassword({ value: '', error: '' });
	};

	const handleDisableMFA = async () => {
		if (!isAdmin || !username) {
			notifyAccountSupportError(
				FRONTEND_ACCOUNT_ERROR_CODES.AUTHORIZATION_FAILED,
				'Account Page / Disable MFA Authorization Failed',
				'Admin privileges required for disabling MFA'
			);
			return;
		}

		if (window.confirm('Are you sure you want to disable MFA for your account? This will remove the extra layer of security.')) {
			try {
				const data = await dispatch(disableMFA(username));
				
				if (data && data.status) {
					toast.success(data.msg || 'MFA disabled successfully');
					clearAccountSupportError(true);
					// Refresh MFA status
					const statusData = await dispatch(getMFAStatus());
					if (statusData && statusData.status) {
						setMfaStatus(statusData.mfa_status);
						clearAccountSupportError(true);
					} else {
						setAccountSupportError(
							FRONTEND_ACCOUNT_ERROR_CODES.MFA_STATUS_LOAD_FAILED,
							'Account Page / MFA Status Refresh Failed',
							statusData?.msg || (typeof statusData === 'string' ? statusData : 'MFA status refresh did not return success')
						);
					}
				} else {
					notifyAccountSupportError(
						FRONTEND_ACCOUNT_ERROR_CODES.MFA_DISABLE_FAILED,
						'Account Page / Disable MFA Failed',
						data?.msg || (typeof data === 'string' ? data : 'Disable MFA request did not return success')
					);
				}
			} catch (err) {
				notifyAccountSupportError(
					FRONTEND_ACCOUNT_ERROR_CODES.MFA_DISABLE_FAILED,
					'Account Page / Disable MFA Request Error',
					err?.message || String(err)
				);
			}
		}
	};

	return (
		<Content currentMenu='none' headerTitle='My Account'>
			{supportErrorMessage && (
				<div className={AccountStyles.supportErrorBanner}>
					<div className={AccountStyles.supportErrorBannerText}>
						<strong>Support Error:</strong>
						<code className={AccountStyles.supportErrorCode}>{supportErrorMessage}</code>
					</div>
					<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
						<button
							type='button'
							className={AccountStyles.copySupportErrorButton}
							onClick={handleCopySupportError}
						>
							{copiedSupportError ? 'Copied' : 'Copy Error Details'}
						</button>
						<button
							type='button'
							className={AccountStyles.copySupportErrorButton}
							onClick={() => clearAccountSupportError(true)}
						>
							Clear
						</button>
					</div>
				</div>
			)}
			<div className={AccountStyles.my_account}>
				<div className={AccountStyles.form_container}>
					{/* Theme Settings Section */}
					<div className={AccountStyles.theme_section}>
						<h3 className={AccountStyles.section_title}>Appearance</h3>
						<div className={AccountStyles.theme_setting}>
							<div className={AccountStyles.theme_info}>
								<label className={AccountStyles.theme_label}>
									Dark Mode
								</label>
								<p className={AccountStyles.theme_description}>
									{isDarkMode ? 'Dark theme is enabled' : 'Light theme is enabled'}
								</p>
							</div>
							<ThemeToggle />
						</div>
					</div>

					{/* MFA Section */}
					<div className={AccountStyles.password_section}>
						<h3 className={AccountStyles.section_title}>Multi-Factor Authentication</h3>
						<div style={{ marginBottom: '20px' }}>
							<p style={{ marginBottom: '10px', color: 'var(--text-secondary)' }}>
								MFA adds an extra layer of security to your account by requiring a verification code
								from your authenticator app in addition to your password.
							</p>
							{mfaLoading ? (
								<p style={{ color: 'var(--text-secondary)' }}>Loading MFA status...</p>
							) : mfaStatus ? (
								<div>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '10px',
											marginBottom: '10px',
										}}>
										<span
											style={{
												padding: '4px 12px',
												borderRadius: '4px',
												backgroundColor: mfaStatus.enabled ? 'var(--success-color)' : 'var(--warning-color)',
												color: 'white',
												fontSize: '14px',
												fontWeight: '600',
											}}>
											{mfaStatus.enabled ? 'Enabled' : 'Disabled'}
										</span>
										{mfaStatus.setup_date && (
											<span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
												Setup on: {new Date(mfaStatus.setup_date).toLocaleDateString()}
											</span>
										)}
									</div>
									{mfaStatus.enabled ? (
										<div>
											<h4 style={{ marginTop: '20px', marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>
												Reset MFA
											</h4>
											<p style={{ marginBottom: '10px', color: 'var(--text-secondary)', fontSize: '14px' }}>
												If you've lost access to your authenticator app or want to use a new device,
												you can reset your MFA setup.
											</p>
											<TextInput
												label='Current Password'
												type='password'
												icon='lock'
												placeholder='Enter your password to reset MFA'
												value={regeneratePassword.value}
												onChange={(e) => setRegeneratePassword({ value: e.target.value, error: '' })}
												errorMessage={regeneratePassword.error}
											/>
											<Button
												label='Reset MFA'
												onClick={handleRegenerateMFA}
												style={{ marginTop: '10px' }}
											/>
											
											{isAdmin && (
												<div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid var(--border-color)' }}>
													<h4 style={{ marginBottom: '10px', fontSize: '14px', fontWeight: '600', color: 'var(--error-color)' }}>
														Admin: Disable MFA
													</h4>
													<p style={{ marginBottom: '10px', color: 'var(--text-secondary)', fontSize: '14px' }}>
														As an administrator, you can completely disable MFA for your account without requiring a password.
														This will remove the extra layer of security from your account.
													</p>
													<Button
														label='Disable MFA'
														onClick={handleDisableMFA}
														style={{ marginTop: '10px', backgroundColor: 'var(--error-color)' }}
													/>
												</div>
											)}
										</div>
									) : (
										<div>
											<h4 style={{ marginTop: '20px', marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>
												Enable MFA
											</h4>
											<p style={{ marginBottom: '10px', color: 'var(--text-secondary)', fontSize: '14px' }}>
												Protect your account with an extra layer of security. You'll need to enter a code
												from your authenticator app in addition to your password when you log in.
											</p>
											<TextInput
												label='Current Password'
												type='password'
												icon='lock'
												placeholder='Enter your password to enable MFA'
												value={regeneratePassword.value}
												onChange={(e) => setRegeneratePassword({ value: e.target.value, error: '' })}
												errorMessage={regeneratePassword.error}
											/>
											<Button
												label='Enable MFA'
												onClick={handleRegenerateMFA}
												style={{ marginTop: '10px' }}
											/>
										</div>
									)}
								</div>
							) : (
								<p style={{ color: 'var(--error-color)' }}>Failed to load MFA status</p>
							)}
						</div>
					</div>

					{/* Password Change Section */}
					<div className={AccountStyles.password_section}>
						<h3 className={AccountStyles.section_title}>Change Password</h3>
						<TextInput
							label='Current Password'
							type='password'
							icon='lock'
							placeholder='Current Password'
							value={currentPassword.value}
							onChange={(e) => setCurrentPassword({ value: e.target.value })}
							errorMessage={currentPassword.error}
						/>
						<div className={AccountStyles.flex}>
							<TextInput
								label='New Password'
								type='password'
								icon='lock'
								placeholder='New Password'
								value={newPassword.value}
								onChange={(e) => setNewPassword({ value: e.target.value })}
								errorMessage={newPassword.error}
							/>
							<TextInput
								label='Confirm Password'
								type='password'
								icon='lock'
								placeholder='Confirm Password'
								value={confirmPassword.value}
								onChange={(e) => setConfirmPassword({ value: e.target.value })}
								errorMessage={confirmPassword.error}
							/>
						</div>
						<Button label='Change Password' onClick={handleChangePassword} />
					</div>

					<button className={AccountStyles.logout_btn} onClick={() => dispatch(logout())}>
						Want to logout from your account? <span>Click to logout</span>
					</button>
				</div>
				<div className={AccountStyles.password_tips}>
					<h2>Tips for your new password</h2>

					<p>Password must contain the following:</p>
					<p>- Atleat 1 lowercase letter,</p>
					<p>- Atleast 1 capital/uppercase letter,</p>
					<p>- Atleast 1 number,</p>
					<p>- Atleast 1 special character,</p>
					<p>- Minimum 12 characters</p>
				</div>
			</div>

			<MFASetupModal
				isOpen={showMFASetupModal}
				qrCode={mfaQrCode}
				secret={mfaSecret}
				onVerify={handleMFAVerify}
				onClose={handleCloseMFAModal}
				loading={mfaVerifying}
				error={mfaSetupError}
			/>
		</Content>
	);
};

export default AccountScreen;
